import { useApi } from './api';
import type { 
  ClaimEvidenceDto, 
  ClaimEvidenceCreateDto, 
  FileUploadResult, 
  ClaimEvidenceResponse 
} from '../types/claimEvidence';

export const useClaimEvidenceService = () => {
  const { apiFetch } = useApi();

  /**
   * Subir archivo a Azure Storage
   */
  const uploadEvidenceFile = async (claimId: number, file: File): Promise<FileUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await apiFetch(`/claims/${claimId}/evidence/upload`, {
      method: 'POST',
      body: formData,
      // NO incluir Content-Type header, el navegador lo agrega automáticamente con el boundary
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al subir archivo' }));
      throw new Error(error.error || error.message || 'Error al subir archivo');
    }

    return res.json();
  };

  /**
   * Crear evidencia de archivo
   */
  const createFileEvidence = async (
    claimId: number,
    fileInfo: FileUploadResult['fileInfo'],
    description?: string
  ): Promise<ClaimEvidenceDto> => {
    const createDto: ClaimEvidenceCreateDto = {
      evidenceType: 'FILE' as const,
      fileName: fileInfo.fileName,
      filePath: fileInfo.filePath,
      fileUrl: fileInfo.fileUrl,
      mimeType: fileInfo.mimeType,
      fileSize: fileInfo.fileSize,
      description: description || ''
    };

    const res = await apiFetch(`/claims/${claimId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(createDto),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al crear evidencia' }));
      throw new Error(error.error || error.message || 'Error al crear evidencia');
    }

    const result: ClaimEvidenceResponse = await res.json();
    return result.data;
  };

  /**
   * Crear evidencia de correo electrónico
   */
  const createEmailEvidence = async (
    claimId: number,
    emailData: {
      subject: string;
      from: string;
      to: string;
      body: string;
      date: string;
    },
    description?: string
  ): Promise<ClaimEvidenceDto> => {
    const createDto: ClaimEvidenceCreateDto = {
      evidenceType: 'EMAIL' as const,
      emailSubject: emailData.subject,
      emailFrom: emailData.from,
      emailTo: emailData.to,
      emailBody: emailData.body,
      emailDate: emailData.date,
      description: description || ''
    };

    const res = await apiFetch(`/claims/${claimId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(createDto),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al crear evidencia' }));
      throw new Error(error.error || error.message || 'Error al crear evidencia');
    }

    const result: ClaimEvidenceResponse = await res.json();
    return result.data;
  };

  /**
   * Obtener todas las evidencias de un claim
   */
  const getClaimEvidences = async (claimId: number): Promise<ClaimEvidenceDto[]> => {
    const res = await apiFetch(`/claims/${claimId}/evidence`);

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al obtener evidencias' }));
      throw new Error(error.error || error.message || 'Error al obtener evidencias');
    }

    return res.json();
  };

  /**
   * Obtener una evidencia específica
   */
  const getEvidenceById = async (claimId: number, evidenceId: number): Promise<ClaimEvidenceDto> => {
    const res = await apiFetch(`/claims/${claimId}/evidence/${evidenceId}`);

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al obtener evidencia' }));
      throw new Error(error.error || error.message || 'Error al obtener evidencia');
    }

    return res.json();
  };

  /**
   * Eliminar evidencia
   */
  const deleteEvidence = async (claimId: number, evidenceId: number): Promise<void> => {
    const res = await apiFetch(`/claims/${claimId}/evidence/${evidenceId}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al eliminar evidencia' }));
      throw new Error(error.error || error.message || 'Error al eliminar evidencia');
    }
  };

  /**
   * Flujo completo: Subir archivo y crear evidencia
   */
  const uploadAndCreateEvidence = async (
    claimId: number,
    file: File,
    description?: string
  ): Promise<ClaimEvidenceDto> => {
    // Paso 1: Subir archivo
    const uploadResult = await uploadEvidenceFile(claimId, file);
    
    // Paso 2: Crear evidencia
    const evidence = await createFileEvidence(claimId, uploadResult.fileInfo, description);
    
    return evidence;
  };

  return {
    uploadEvidenceFile,
    createFileEvidence,
    createEmailEvidence,
    getClaimEvidences,
    getEvidenceById,
    deleteEvidence,
    uploadAndCreateEvidence
  };
};

